import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useDebounce } from './useDebounce';

export interface SearchResultItem {
  id: number;
  entityType: string;
  entityId: number;
  title: string;
  description?: string;
  contentPreview?: string;
  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
  iconType?: string;
  entityCode?: string;
  extraData?: Record<string, unknown>;
  rank?: number;
  highlightTitle?: string;
  highlightDescription?: string;
  navigateTo: string;
}

export interface SearchResponse {
  results: SearchResultItem[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export type SearchTabType = 'ALL' | 'USER' | 'CLASSROOM' | 'ASSIGNMENT' | 'EXERCISE_TEMPLATE';

const SEARCH_HISTORY_KEY = 'global-search-history';
const SEARCH_STATE_KEY = 'global-search-last-state';

type StoredSearchState = {
  query: string;
  selectedType: SearchTabType;
  resultsByType: Partial<
    Record<
      SearchTabType,
      {
        results: SearchResultItem[];
        totalCount: number;
        offset: number;
      }
    >
  >;
  timestamp: number;
};

const readStoredJson = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

const writeStoredJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

const buildCountsFromResultsByType = (
  resultsByType: StoredSearchState['resultsByType'] | undefined,
  fallbackResults?: SearchResultItem[],
) => {
  const counts: Record<string, number> = {};
  if (resultsByType) {
    (['USER', 'CLASSROOM', 'ASSIGNMENT', 'EXERCISE_TEMPLATE'] as SearchTabType[]).forEach(
      (type) => {
        const bucket = resultsByType[type];
        if (!bucket) return;
        counts[type] = bucket.totalCount ?? bucket.results?.length ?? 0;
      },
    );
  } else if (fallbackResults) {
    fallbackResults.forEach((result) => {
      counts[result.entityType] = (counts[result.entityType] ?? 0) + 1;
    });
  }

  return Object.keys(counts).length ? counts : null;
};

export const useGlobalSearch = ({ pageSize = 10 } = {}) => {
  const [query, setQuery] = useState('');
  const [selectedType, setSelectedType] = useState<SearchTabType>('ALL');
  const [offset, setOffset] = useState(0);
  const [accumulatedResults, setAccumulatedResults] = useState<SearchResultItem[]>([]);
  const [cachedTotalCount, setCachedTotalCount] = useState<number | null>(null);
  const [cachedTypeCounts, setCachedTypeCounts] = useState<Record<string, number> | null>(null);
  const [isRestoringCache, setIsRestoringCache] = useState(false);
  const isHydratingRef = useRef(false);
  const debouncedQuery = useDebounce(query, 300);

  const buildSearchParams = useCallback(
    (entityType?: SearchTabType, nextOffset?: number) => {
      const params = new URLSearchParams();
      params.set('query', debouncedQuery);
      params.set('limit', String(pageSize));
      params.set('offset', String(nextOffset ?? offset));
      if (entityType && entityType !== 'ALL') {
        params.append('entityTypes', entityType);
      }
      return params.toString();
    },
    [debouncedQuery, offset, pageSize],
  );

  const {
    data: searchData,
    isLoading,
    isFetching,
  } = useQuery<SearchResponse>({
    queryKey: ['global-search', debouncedQuery, selectedType, offset],
    queryFn: () => apiGet<SearchResponse>(`/api/search?${buildSearchParams(selectedType, offset)}`),
    enabled: debouncedQuery.length >= 2 && !isRestoringCache,
    staleTime: 30 * 1000,
  });

  const { data: typeCounts } = useQuery<Record<string, number>>({
    queryKey: ['global-search-counts', debouncedQuery],
    queryFn: async () => {
      const entityTypes: SearchTabType[] = ['USER', 'CLASSROOM', 'ASSIGNMENT', 'EXERCISE_TEMPLATE'];
      const entries = await Promise.all(
        entityTypes.map(async (entityType) => {
          const params = new URLSearchParams();
          params.set('query', debouncedQuery);
          params.set('limit', '1');
          params.set('offset', '0');
          params.append('entityTypes', entityType);
          const res = await apiGet<SearchResponse>(`/api/search?${params.toString()}`);
          return [entityType, res.totalCount] as const;
        }),
      );
      return Object.fromEntries(entries);
    },
    enabled: debouncedQuery.length >= 2 && !isRestoringCache,
    staleTime: 30 * 1000,
  });

  const totalCount = searchData?.totalCount ?? cachedTotalCount ?? 0;
  const effectiveTypeCounts = typeCounts ?? cachedTypeCounts;
  const allCount = useMemo(() => {
    if (!effectiveTypeCounts) return totalCount;
    return (
      (effectiveTypeCounts.USER ?? 0) +
      (effectiveTypeCounts.CLASSROOM ?? 0) +
      (effectiveTypeCounts.ASSIGNMENT ?? 0) +
      (effectiveTypeCounts.EXERCISE_TEMPLATE ?? 0)
    );
  }, [effectiveTypeCounts, totalCount]);

  const storeSearchHistory = useCallback((term: string) => {
    if (!term || term.trim().length < 2) return;
    const trimmed = term.trim();
    const current = readStoredJson<string[]>(SEARCH_HISTORY_KEY) ?? [];
    const next = [trimmed, ...current.filter((item) => item !== trimmed)].slice(0, 10);
    writeStoredJson(SEARCH_HISTORY_KEY, next);
  }, []);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }
    const storedState = readStoredJson<StoredSearchState>(SEARCH_STATE_KEY);
    const matchesQuery =
      storedState?.query && debouncedQuery && storedState.query === debouncedQuery;
    const cachedBucket = matchesQuery ? storedState?.resultsByType?.[selectedType] : undefined;

    if (cachedBucket) {
      setIsRestoringCache(true);
      setOffset(cachedBucket.offset ?? 0);
      setAccumulatedResults(cachedBucket.results ?? []);
      setCachedTotalCount(cachedBucket.totalCount ?? 0);
      const fallbackResults =
        storedState?.resultsByType?.ALL?.results ??
        (storedState as unknown as { results?: SearchResultItem[] }).results ??
        undefined;
      setCachedTypeCounts(
        buildCountsFromResultsByType(storedState?.resultsByType, fallbackResults),
      );
      return;
    }

    setIsRestoringCache(false);
    setOffset(0);
    setAccumulatedResults([]);
    setCachedTotalCount(null);
    setCachedTypeCounts(null);
  }, [debouncedQuery, selectedType]);

  useEffect(() => {
    if (!searchData) return;

    if (offset === 0) {
      storeSearchHistory(debouncedQuery);
    }

    setCachedTotalCount(searchData.totalCount);
    setAccumulatedResults((prev) => {
      const nextResults = offset === 0 ? searchData.results : [...prev, ...searchData.results];
      const storedState = readStoredJson<StoredSearchState>(SEARCH_STATE_KEY);
      const nextResultsByType = {
        ...(storedState?.resultsByType ?? {}),
        [selectedType]: {
          results: nextResults,
          totalCount: searchData.totalCount,
          offset,
        },
      };
      writeStoredJson(SEARCH_STATE_KEY, {
        query: debouncedQuery,
        selectedType,
        resultsByType: nextResultsByType,
        timestamp: Date.now(),
      });
      return nextResults;
    });
  }, [searchData, offset, debouncedQuery, selectedType, storeSearchHistory]);

  const restoreFromCache = useCallback(() => {
    const storedState = readStoredJson<StoredSearchState>(SEARCH_STATE_KEY);
    const trimmedQuery = query.trim();

    if (storedState?.query && (!trimmedQuery || trimmedQuery === storedState.query)) {
      isHydratingRef.current = true;
      setIsRestoringCache(true);
      if (trimmedQuery !== storedState.query) {
        setQuery(storedState.query);
      }
      const restoredType = storedState.selectedType ?? 'ALL';
      const restoredBucket = storedState.resultsByType?.[restoredType];
      const legacyResults = (storedState as unknown as { results?: SearchResultItem[] }).results;
      const legacyTotalCount = (storedState as unknown as { totalCount?: number }).totalCount;
      const legacyOffset = (storedState as unknown as { offset?: number }).offset;
      setSelectedType(restoredType);
      setOffset(restoredBucket?.offset ?? legacyOffset ?? 0);
      setAccumulatedResults(restoredBucket?.results ?? legacyResults ?? []);
      setCachedTotalCount(restoredBucket?.totalCount ?? legacyTotalCount ?? 0);
      const fallbackResults = storedState.resultsByType?.ALL?.results ?? legacyResults ?? undefined;
      setCachedTypeCounts(buildCountsFromResultsByType(storedState.resultsByType, fallbackResults));
      return;
    }

    if (trimmedQuery.length >= 2) {
      setIsRestoringCache(false);
      return;
    }

    if (accumulatedResults.length > 0) {
      setIsRestoringCache(true);
    }
  }, [accumulatedResults.length, query]);

  const updateQuery = useCallback((value: string) => {
    setIsRestoringCache(false);
    setQuery(value);
  }, []);

  const clearSearchState = useCallback(() => {
    setIsRestoringCache(false);
    setQuery('');
    setAccumulatedResults([]);
    setCachedTotalCount(null);
    setOffset(0);
    setSelectedType('ALL');
  }, []);

  const loadMore = useCallback(() => {
    if (!searchData?.hasMore || isFetching) return;
    setIsRestoringCache(false);
    setOffset((prev) => prev + pageSize);
  }, [isFetching, pageSize, searchData?.hasMore]);

  const selectType = useCallback((type: SearchTabType) => {
    setIsRestoringCache(false);
    setSelectedType(type);
  }, []);

  return {
    query,
    debouncedQuery,
    selectedType,
    offset,
    results: accumulatedResults,
    totalCount,
    effectiveTypeCounts,
    allCount,
    isLoading,
    isFetching,
    hasMore: searchData?.hasMore ?? false,
    updateQuery,
    clearSearchState,
    loadMore,
    restoreFromCache,
    selectType,
    setSelectedType,
    setOffset,
  };
};
